import type { InputSource } from '../types';
import { drawStatusMessage } from '../utils/draw-status-message';

function destroyVideo(userData: Record<string, any>) {
  if (userData._videoRaf) {
    cancelAnimationFrame(userData._videoRaf);
    userData._videoRaf = null;
  }
  if (userData._videoEl) {
    userData._videoEl.pause();
    userData._videoEl = null;
    userData._videoSrc = null;
  }
}

export const source: InputSource = {
  label: 'Source',
  value: 'source',
  draw: (ctx, width, height, userData) => {
    const drawLoading = () => {
      drawStatusMessage(ctx, width, height, 'Loading...');
      if (userData.texture) userData.texture.needsUpdate = true;
      userData.render?.();
    };

    const drawError = () => {
      drawStatusMessage(ctx, width, height, 'Failed to load asset', '#a00');
      if (userData.texture) userData.texture.needsUpdate = true;
      userData.render?.();
    };

    if (userData?.sourceLoading) {
      drawLoading();
      return;
    }

    if (userData?.sourceUrl && userData?.sourceAssetType) {
      const { sourceUrl, sourceAssetType: type } = userData;

      // ── Image ──────────────────────────────────────────────────────────
      if (type === 'IM') {
        destroyVideo(userData);

        if (userData._imgSrc !== sourceUrl) {
          userData._imgSrc = sourceUrl;
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          drawLoading();
          img.onload = () => {
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            if (userData.texture) userData.texture.needsUpdate = true;
            userData.render?.();
          };
          img.onerror = () => {
            drawError();
          };
          img.src = sourceUrl;
        }
        return;
      }
      if (type === 'VI') {
        if (userData._videoSrc && userData._videoSrc !== sourceUrl) {
          destroyVideo(userData);
        }

        if (!userData._videoEl) {
          userData._videoSrc = sourceUrl;

          const video = userData.textureManager?.getVideoElement(sourceUrl);
          if (!video) return;

          video.autoplay = false;
          video.muted = true;
          video.playsInline = true;
          video.loop = false;
          video.pause();

          drawLoading();

          // Track state for initialization and looping
          let wasInRange = false;
          let videoInitialized = false;

          const drawFrame = () => {
            userData._videoRaf = requestAnimationFrame(drawFrame);

            const timelinePlayer = userData.timelinePlayer;
            if (!timelinePlayer) return;

            const currentTime = timelinePlayer.currentTime;
            const mediaStartTime = userData.mediaStartTime ?? 0;
            const mediaStart = userData.mediaStart ?? 0;
            const mediaDuration = userData.mediaDuration ?? 10;
            const mediaEndTime = mediaStartTime + mediaDuration;
            const isInRange = currentTime >= mediaStartTime && currentTime <= mediaEndTime;

            // Initialize video position when entering the media block range
            if (isInRange && !wasInRange && !videoInitialized) {
              videoInitialized = true;
              if (video.readyState >= 2) {
                video.currentTime = mediaStart;
              }
            }

            // Reset initialization flag when exiting range so it can reinit on next entry
            if (!isInRange && wasInRange) {
              videoInitialized = false;
            }

            // Handle play/pause based on timeline state and block range
            if (timelinePlayer.isPlaying && isInRange) {
              if (video.paused) {
                video.play().catch(() => {});
              }
              // Check if video reached its end while playing (natural loop)
              if (video.duration && video.currentTime >= video.duration - 0.1) {
                video.currentTime = mediaStart;
              }
            } else {
              if (!video.paused) {
                video.pause();
              }
            }

            // Draw frame to canvas
            if (video.readyState >= video.HAVE_CURRENT_DATA && isInRange) {
              ctx.clearRect(0, 0, width, height);
              ctx.drawImage(video, 0, 0, width, height);
              if (userData.texture) userData.texture.needsUpdate = true;
              userData.render?.();
            } else if (!isInRange) {
              ctx.fillStyle = '#000';
              ctx.fillRect(0, 0, width, height);
              if (userData.texture) userData.texture.needsUpdate = true;
              userData.render?.();
            }

            wasInRange = isInRange;
          };

          video.addEventListener(
            'canplay',
            () => {
              if (!userData._videoRaf) drawFrame();
            },
            { once: true }
          );

          if (video.readyState >= video.HAVE_CURRENT_DATA) {
            drawFrame();
          }

          video.onerror = () => {
            drawError();
          };

          userData._videoEl = video;
        }
        return;
      }
    }

    // ── No asset selected ───────────────────────────────────────────
    drawStatusMessage(ctx, width, height, 'Select an asset');
    if (userData?.texture) userData.texture.needsUpdate = true;
    userData?.render?.();
  },
  settings: [{ label: 'Source', key: 'source', type: 'asset' }],
};
